import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorText } from '../api';
import { useAuth } from '../auth';
import { KundenAuswahl } from '../components/KundenAuswahl';
import { Card, Field, Notice } from '../components/ui';
import type { BenutzerKurz, Kunde, Serviceauftrag, Standort } from '../types';

const GERAETEARTEN = ['Waschmaschine', 'Trockner', 'Geschirrspüler', 'Kühlschrank', 'Herd / Backofen', 'Kaffeevollautomat', 'Staubsauger', 'Fernseher', 'Notebook / PC', 'Smartphone', 'Kleingerät'];

/** Geräteannahme in der Werkstatt. Danach direkt der Annahmebeleg für den Kunden. */
export function ServiceNeuPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [kunde, setKunde] = useState<Kunde | null>(null);
  const aussendienst = params.get('art') === 'aussendienst';
  const [d, setD] = useState({
    geraeteart: '',
    marke: '',
    modell: '',
    seriennummer: '',
    zubehoer: '',
    fehlerbeschreibung: '',
    wertgarantie: false,
    wertgarantieNr: '',
    notiz: '',
    standortId: '',
    zustaendigId: '',
  });
  const [standorte, setStandorte] = useState<Standort[]>([]);
  const [benutzer, setBenutzer] = useState<BenutzerKurz[]>([]);
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api<Standort[]>('/api/standorte').then(setStandorte);
    void api<BenutzerKurz[]>('/api/benutzer/auswahl').then((b) => {
      setBenutzer(b);
      // Vorschlag: der erste Techniker, sonst ich.
      setD((alt) => ({ ...alt, zustaendigId: b.find((x) => x.rolle === 'techniker')?.id ?? user?.id ?? '' }));
    });
    const kid = params.get('kunde');
    if (kid) void api<{ kunde: Kunde }>(`/api/kunden/${kid}`).then((r) => setKunde(r.kunde));
  }, [params, user?.id]);

  const setze = (feld: keyof typeof d, wert: string | boolean) => setD((alt) => ({ ...alt, [feld]: wert }));

  async function anlegen() {
    if (!kunde) return;
    setBusy(true);
    setFehler('');
    try {
      const s = await api<Serviceauftrag>('/api/service', {
        method: 'POST',
        body: {
          ...d,
          art: aussendienst ? 'aussendienst' : 'werkstatt',
          kundeId: kunde.id,
          standortId: d.standortId || null,
          wertgarantieNr: d.wertgarantie ? d.wertgarantieNr : null,
        },
      });
      navigate(aussendienst ? `/service/${s.id}` : `/service/${s.id}?neu=1`);
    } catch (e) {
      setFehler(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="kopf">
        <h1>{aussendienst ? '🔧 Außendienstauftrag' : '🔧 Gerät annehmen'}</h1>
        <span className="spacer" />
        <Link to={aussendienst ? '/service/neu' : '/service/neu?art=aussendienst'} className="small">
          {aussendienst ? 'Stattdessen Gerät in der Werkstatt annehmen' : 'Stattdessen Außendienst beim Kunden'}
        </Link>
      </div>
      {fehler && <Notice kind="error">{fehler}</Notice>}
      <div className="stack" style={{ maxWidth: 900 }}>
        <Card title="1. Kunde">
          <KundenAuswahl gewaehlt={kunde} onWahl={setKunde} />
        </Card>
        <Card title="2. Gerät und Fehler">
          <div className="stack" style={{ gap: 12 }}>
            <div className="grid3">
              <Field label="Geräteart (Pflicht)">
                <input value={d.geraeteart} onChange={(e) => setze('geraeteart', e.target.value)} list="geraetearten" />
                <datalist id="geraetearten">
                  {GERAETEARTEN.map((g) => (
                    <option key={g} value={g} />
                  ))}
                </datalist>
              </Field>
              <Field label="Marke">
                <input value={d.marke} onChange={(e) => setze('marke', e.target.value)} />
              </Field>
              <Field label="Modell">
                <input value={d.modell} onChange={(e) => setze('modell', e.target.value)} />
              </Field>
            </div>
            <div className="grid2">
              <Field label="Seriennummer">
                <input value={d.seriennummer} onChange={(e) => setze('seriennummer', e.target.value)} />
              </Field>
              {!aussendienst && (
                <Field label="Mitgegebenes Zubehör" hint="Steht auf dem Annahmebeleg">
                  <input value={d.zubehoer} onChange={(e) => setze('zubehoer', e.target.value)} placeholder="z. B. Netzteil, Wassertank" />
                </Field>
              )}
            </div>
            <Field label="Fehlerbeschreibung des Kunden (Pflicht)">
              <textarea value={d.fehlerbeschreibung} onChange={(e) => setze('fehlerbeschreibung', e.target.value)} />
            </Field>
            <label className="row">
              <input type="checkbox" style={{ width: 'auto' }} checked={d.wertgarantie} onChange={(e) => setze('wertgarantie', e.target.checked)} />
              Wertgarantie-Fall
            </label>
            {d.wertgarantie && (
              <Field label="Wertgarantie-Vertragsnummer (Pflicht)">
                <input value={d.wertgarantieNr} onChange={(e) => setze('wertgarantieNr', e.target.value)} />
              </Field>
            )}
          </div>
        </Card>
        <Card title="3. Angaben">
          <div className="grid2">
            <Field label="Standort">
              <select value={d.standortId} onChange={(e) => setze('standortId', e.target.value)}>
                <option value="">—</option>
                {standorte.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Zuständiger Techniker">
              <select value={d.zustaendigId} onChange={(e) => setze('zustaendigId', e.target.value)}>
                {benutzer.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </Field>
            <div className="span2">
              <Field label="Interne Notiz">
                <textarea value={d.notiz} onChange={(e) => setze('notiz', e.target.value)} style={{ minHeight: 60 }} />
              </Field>
            </div>
          </div>
        </Card>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button onClick={() => navigate(-1)}>Abbrechen</button>
          <button
            className="primary"
            disabled={busy || !kunde || !d.geraeteart.trim() || d.fehlerbeschreibung.trim().length < 3 || (d.wertgarantie && !d.wertgarantieNr.trim())}
            onClick={() => void anlegen()}
          >
            {aussendienst ? 'Auftrag anlegen' : 'Annehmen und Beleg drucken'}
          </button>
        </div>
      </div>
    </>
  );
}
